import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Video } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/Logo";
import { SERVICES, CONTACT, API } from "@/data/services";

const SERVICE_OPTIONS = [
  "Book an appointment",
  ...SERVICES.map((s) => s.name),
  "Eucalypt Calm app",
  "Referrer enquiry",
  "Not sure yet",
];

const inputClass =
  "w-full rounded-xl border border-euca-line bg-euca-mist px-4 py-3 text-base text-charcoal transition-all focus:border-euca focus:outline-none focus:ring-1 focus:ring-euca";

const INFO = [
  { icon: MapPin, label: "Gold Coast", value: CONTACT.address, href: null, id: "contact-address" },
  { icon: MapPin, label: "Canberra", value: CONTACT.addressCanberra, href: null, id: "contact-address-canberra" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "contact-email" },
  { icon: Phone, label: "Call", value: CONTACT.phone, href: CONTACT.phoneHref, id: "contact-phone" },
  { icon: Clock, label: "Opening hours", value: "By appointment, Monday to Friday", href: null, id: "contact-hours" },
  { icon: Video, label: "Telehealth & service areas", value: "Gold Coast, Logan, Brisbane, Canberra and surrounding suburbs. In-home visits available.", href: null, id: "contact-telehealth" },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initial = params.get("service") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: initial === "referral" ? "Referrer enquiry" : initial === "ndis" ? "NDIS Positive Behaviour Support" : initial === "calm-app" ? "Eucalypt Calm app" : "",
    preferred_contact: "email",
    location: "No preference",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, service: form.service || "Not sure yet" });
      toast.success("Thank you — your enquiry has been sent. We will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", preferred_contact: "email", location: "No preference", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -right-10 -top-10 text-euca-badge">
          <Sprig className="h-64 w-64" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <SectionHeading
            eyebrow="Contact"
            title="Book an appointment or send an enquiry"
            lead="Tell us a little about what you are looking for and we will respond within two business days. If you are a referrer, mention your role and the service you have in mind."
          />
        </div>
      </section>

      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <form onSubmit={submit} className="rounded-[2rem] border border-euca-line bg-white p-7 sm:p-10 shadow-sm" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-semibold text-charcoal">Name</label>
                    <input id="name" required value={form.name} onChange={set("name")} className={inputClass} data-testid="contact-name-input" placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-semibold text-charcoal">Email</label>
                    <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputClass} data-testid="contact-email-input" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-charcoal">Phone <span className="font-normal text-mutedsoft">(optional)</span></label>
                    <input id="phone" value={form.phone} onChange={set("phone")} className={inputClass} data-testid="contact-phone-input" placeholder="04xx xxx xxx" />
                  </div>
                  <div>
                    <label htmlFor="service" className="mb-2 block text-sm font-semibold text-charcoal">Service</label>
                    <select id="service" value={form.service} onChange={set("service")} className={inputClass} data-testid="contact-service-select">
                      <option value="">Not sure yet</option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <span className="mb-2 block text-sm font-semibold text-charcoal">Preferred contact</span>
                  <div className="flex gap-6">
                    {["email", "phone"].map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-base text-charcoal/85">
                        <input
                          type="radio"
                          name="preferred_contact"
                          value={opt}
                          checked={form.preferred_contact === opt}
                          onChange={set("preferred_contact")}
                          className="h-4 w-4 accent-euca"
                          data-testid={`contact-prefer-${opt}`}
                        />
                        {opt === "email" ? "Email" : "Phone"}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-5">
                  <span className="mb-2 block text-sm font-semibold text-charcoal">Preferred location</span>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {["Gold Coast (Ashmore)", "Canberra (Deakin)", "Telehealth", "No preference"].map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-base text-charcoal/85">
                        <input
                          type="radio"
                          name="location"
                          value={opt}
                          checked={form.location === opt}
                          onChange={set("location")}
                          className="h-4 w-4 accent-euca"
                          data-testid={`contact-location-${opt.split(" ")[0].toLowerCase()}`}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="message" className="mb-2 block text-sm font-semibold text-charcoal">How can we help?</label>
                  <textarea
                    id="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className={inputClass}
                    data-testid="contact-message-input"
                    placeholder="A brief outline is enough — you do not need to share more than you are comfortable with."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="mt-7 w-full rounded-full bg-euca px-7 py-4 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-euca-dark hover:shadow-md disabled:opacity-60 sm:w-auto"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
                <p className="mt-4 text-xs leading-relaxed text-mutedsoft">
                  Please do not include urgent or crisis information in this form. If you need immediate support,
                  call 000 or Lifeline on 13 11 14.
                </p>
              </form>
            </Reveal>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="rounded-[2rem] bg-euca-dark p-8 sm:p-10" data-testid="contact-info-panel">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca-sage">Practice details</p>
                  <div className="mt-7 space-y-7">
                    {INFO.map(({ icon: Icon, label, value, href, id }) => (
                      <div key={id} data-testid={id} className="flex gap-4">
                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-euca-sage" strokeWidth={1.5} />
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-euca-sage">{label}</p>
                          {href ? (
                            <a href={href} className="mt-1 block text-base text-euca-mist/85 hover:text-euca-mist">{value}</a>
                          ) : (
                            <p className="mt-1 text-base leading-relaxed text-euca-mist/85">{value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="mt-6 rounded-[2rem] border border-euca-line bg-white p-8" data-testid="contact-crisis-note">
                  <p className="text-sm font-semibold text-charcoal">Not a crisis service</p>
                  <p className="mt-2 text-sm leading-relaxed text-mutedsoft">
                    Eucalypt Therapy is not an emergency service. If you are in immediate danger call 000. For 24/7
                    support contact Lifeline (13 11 14), Beyond Blue (1300 22 4636) or Open Arms for veterans and
                    families (1800 011 046).
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
